import { Route as rewards_route } from './rewards';
import { createFileRoute } from '@tanstack/react-router';
import { Button, Checkbox, HStack, Text, VStack } from 'ui_2';
import { z } from 'zod';
import { BiCopy } from 'react-icons/bi';
import { useState } from 'react';
import {
  EthereumRedemptionInstructions,
  ProductRedemptionInstructions,
} from '@src/components/store/ProductRedemptionInstructions';
import { ProductTermsAndConditions } from '@src/components/store/ProductTermsAndConditions';
import { ProductAvailability } from '@src/components/store/ProductAvailability';
import { Page } from '@src/components/layout/Page';
import { toast } from 'react-hot-toast';
import {
  AnalyticsEvents,
  useAnalytics,
  useAnalyticsDefaultValues,
} from '@src/providers/analytics';
import { useProductSKUFields } from '@src/hooks/useProductSkuFields';
import { useReward } from '@src/hooks/useArcadeStore';
import { ErrorMessage } from '@src/components/ErrorMessage';
import { LoadingMessage } from '@src/components/LoadingMessage';
import { ViewProductDetails } from '@src/components/store/ViewProductDetails';
import { Body } from '@src/components/layout/Body';

const rewardSchema = z.object({
  id: z.string(),
  skuTitle: z.string().optional(),
  showHeader: z.boolean().optional().default(true),
  showAccountAndBalance: z.boolean().optional().default(true),
});
export const Route = createFileRoute('/reward')({
  validateSearch: rewardSchema,
  staticData: {
    title: 'Reward',
  },
  head: ({ match }) => ({
    meta: [
      {
        name: 'description',
        content: match.search.skuTitle
          ? `View your ${match.search.skuTitle} purchase`
          : undefined,
      },
      {
        title: match.search.skuTitle ? `${match.search.skuTitle}` : undefined,
      },
    ],
  }),
  component: RouteComponent,
});

const RewardView = () => {
  const { id, skuTitle: _skuTitle } = Route.useSearch();

  const [isCovered, setIsCovered] = useState(true);

  const analytics = useAnalytics();
  const { address, network, partBalance } = useAnalyticsDefaultValues();

  const {
    data: reward,
    error,
    isLoading: rewardIsLoading,
    isMarkingUsed,
    markUsedStatus,
  } = useReward(id);
  const {
    availability,
    redemptionInstructions,
    termsAndConditions,
    smallLogoImageURL,
    skuTitle,
    serviceTitle,
    url,
    serviceSlug,
  } = useProductSKUFields(reward?.productSKU);
  if (error) {
    return <ErrorMessage error={error} fullPageHeight={false} />;
  } else if (rewardIsLoading) {
    return (
      <LoadingMessage
        message={`Loading ${_skuTitle ?? 'reward'}`}
        fullPageHeight={false}
      />
    );
  } else if (reward === null) {
    return (
      <ErrorMessage
        error={`Could not find product for id: ${id}`}
        fullPageHeight={false}
      />
    );
  }

  return (
    <VStack className={`h-full w-full gap-4`}>
      <VStack className='gap-4 p-4 px-4 sm:px-4 md:px-8 lg:px-16 xl:px-24'>
        <HStack className='items-center gap-3'>
          {smallLogoImageURL && (
            <img
              width={70}
              height={70}
              className='rounded-[10px]'
              src={smallLogoImageURL}
              alt={`${skuTitle} small logo`}
            />
          )}
          <VStack className='gap-1'>
            <Text font='bold-md'>{skuTitle}</Text>
            {url && <ViewProductDetails url={url} />}
          </VStack>
        </HStack>
        <VStack className='w-full gap-2'>
          <HStack className='items-center gap-3'>
            <Text font='bold-md'>
              {serviceSlug === 'ethereum'
                ? 'Ethereum Private Key'
                : 'Redemption Code'}
            </Text>
            <button
              onClick={() => {
                void analytics.track(AnalyticsEvents.RewardCopied, {
                  service: serviceTitle,
                  productTitle: skuTitle,
                  address,
                  network,
                  partBalance,
                });
                navigator.clipboard
                  .writeText(reward.code)
                  .then(() => toast.success('Code copied!'));
              }}
            >
              <BiCopy />
            </button>
          </HStack>
          <div className='relative w-full'>
            {isCovered && (
              <div
                className='absolute inset-0 flex cursor-pointer items-center justify-center rounded-md bg-gray-200 text-white hover:bg-gray-300 active:bg-gray-400'
                onClick={() => setIsCovered(false)}
              />
            )}
            <div className='break-words rounded bg-gray-100 p-4'>
              <Text>{reward.code}</Text>
            </div>
          </div>
          <Button variant='gray' onClick={() => setIsCovered(!isCovered)}>
            {isCovered ? 'Reveal Redemption Code' : 'Hide Redemption Code'}
          </Button>
          <Text font='xs' color='gray-600'>
            Store items are for personal use only.
          </Text>
        </VStack>
        {availability && <ProductAvailability availability={availability} />}
        <VStack>
          {serviceSlug === 'ethereum' && <EthereumRedemptionInstructions />}
          {redemptionInstructions && serviceSlug !== 'ethereum' && (
            <ProductRedemptionInstructions
              redemptionInstructions={redemptionInstructions}
              serviceSlug={serviceSlug}
            />
          )}
          {termsAndConditions && (
            <ProductTermsAndConditions
              termsAndConditions={termsAndConditions}
            />
          )}
        </VStack>
      </VStack>
    </VStack>
  );
};

function RouteComponent() {
  const { id, showHeader, showAccountAndBalance } = Route.useSearch();
  const {
    data: reward,
    isMarkingUsed,
    isSuccess,
    markUsedStatus,
  } = useReward(id);

  const MarkAsUsed = () => (
    <HStack className='items-center gap-2'>
      <Checkbox
        id='mark-used'
        disabled={isMarkingUsed}
        checked={reward?.used}
        onCheckedChange={(value) => markUsedStatus(Boolean(value))}
      />
      <label
        htmlFor='mark-used'
        className='w-max peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
      >
        <Text font='sm'>Mark as used</Text>
      </label>
    </HStack>
  );

  if (showHeader) {
    return (
      <Page
        title='Reward'
        backRoute={rewards_route.to}
        rightElement={isSuccess ? <MarkAsUsed /> : undefined}
        showAccountAndBalance={showAccountAndBalance}
      >
        <RewardView />
      </Page>
    );
  }

  return (
    <Body>
      <HStack className='w-full'>
        <HStack className='flex-grow' />
        <MarkAsUsed />
      </HStack>
      <RewardView />
    </Body>
  );
}
